import { Box, Text, VStack, HStack } from "@chakra-ui/layout";
import { Button, NumberDecrementStepper, NumberIncrementStepper, NumberInput, NumberInputField, NumberInputStepper, useColorModeValue } from "@chakra-ui/react";

import { $ok } from "foundation-ts/commons";
import { Nullable } from "foundation-ts/types";

import { G1Input } from "@components/G1Input";
import { useDevice } from "@/hooks/useDevice";
import { $g1Color } from "@/theme/G1Style";
import { G1Icon } from "@/utils/icons";

import { SubscriptionType } from "./RegisterTypes";

interface SubscriptionStepProps {
    selectedSubscription: Nullable<SubscriptionType>;
    isAloneUser: Nullable<boolean>;
    nbCollaborators: number;
    partnerCode: string;
    onSubscriptionChoice: (s: SubscriptionType) => void;
    onAloneUserChange: (value: boolean) => void;
    onCollaboratorsChange: (valueAsString: string, valueAsNumber: number) => void;
    onPartnerCodeChange: (code: string) => void;
}

export const SubscriptionStep = (props: SubscriptionStepProps) => {
    const {
        selectedSubscription,
        isAloneUser,
        nbCollaborators,
        partnerCode,
        onSubscriptionChoice,
        onAloneUserChange,
        onCollaboratorsChange,
        onPartnerCodeChange,
    } = props;
    const { isMOBILE } = useDevice();

    const limitedBorderColor = useColorModeValue("blue.400", "blue.100");
    const limitedBgColor = useColorModeValue("blue.50", "blue.400");
    const unlimitedBorderColor = useColorModeValue("green.400", "green.50");
    const unlimitedBgColor = useColorModeValue("green.50", "green.400");
    const limitedActiveTextColor = useColorModeValue("blue.600", "blue.100");
    const unlimitedActiveTextColor = useColorModeValue("green.600", "green.100");
    const textColor = useColorModeValue("gray.100", "gray.400");
    const btnTitleColor = useColorModeValue("gray.100", "gray.400");

    return (
        <VStack spacing={3}>
            <HStack spacing={4} align="stretch" width="100%">
                <Box
                    as="button"
                    p={isMOBILE ? 1 : 5}
                    flex="1"
                    borderWidth="2px"
                    borderRadius="lg"
                    borderColor={selectedSubscription === "limited" ? limitedBorderColor : "gray.200"}
                    bg={selectedSubscription === "limited" ? limitedBgColor : "transparent"}
                    _hover={{ borderColor: limitedBorderColor, bg: limitedBgColor, "& .btn-title": { color: limitedActiveTextColor }, "& .btn-text": { color: limitedActiveTextColor } }}
                    transition="all 0.2s"
                    onClick={() => onSubscriptionChoice("limited")}
                >
                    <HStack justifyContent="center">
                        <Text className="btn-title" fontSize={["sm", "2xl"]} fontWeight="bold" mb={2}
                            color={selectedSubscription === "limited" ? limitedActiveTextColor : btnTitleColor}
                            transition="color 0.2s">
                            Abonnement 60
                        </Text>
                    </HStack>
                    <Text className="btn-text" fontSize={["sm", "lg"]} fontWeight="bold"
                        color={selectedSubscription === "limited" ? limitedActiveTextColor : textColor}
                        transition="color 0.2s">109€ HT / an</Text>
                    <HStack mt={2}><G1Icon.Check /><Text className="btn-text" fontSize={["xs", "sm"]} color={selectedSubscription === "limited" ? limitedActiveTextColor : textColor} transition="color 0.2s">Pour 1 utilisateur</Text></HStack>
                    <HStack><G1Icon.Check /><Text className="btn-text" fontSize={["xs", "sm"]} color={selectedSubscription === "limited" ? limitedActiveTextColor : textColor} transition="color 0.2s">60 signatures incluses</Text></HStack>
                    <HStack><G1Icon.Check /><Text className="btn-text" fontSize={["xs", "sm"]} color={selectedSubscription === "limited" ? limitedActiveTextColor : textColor} transition="color 0.2s">Inclus 1 mois gratuit</Text></HStack>
                </Box>

                <Box
                    as="button"
                    p={isMOBILE ? 1 : 5}
                    flex="1"
                    borderWidth="2px"
                    borderRadius="lg"
                    borderColor={selectedSubscription === "unlimited" ? unlimitedBorderColor : "gray.200"}
                    bg={selectedSubscription === "unlimited" ? unlimitedBgColor : "transparent"}
                    _hover={{ borderColor: unlimitedBorderColor, bg: unlimitedBgColor, "& .btn-title": { color: unlimitedActiveTextColor }, "& .btn-text": { color: unlimitedActiveTextColor } }}
                    transition="all 0.2s"
                    onClick={() => onSubscriptionChoice("unlimited")}
                >
                    <HStack justifyContent="center">
                        <Text className="btn-title" fontSize={["sm", "2xl"]} fontWeight="bold" mb={2}
                            color={selectedSubscription === "unlimited" ? unlimitedActiveTextColor : btnTitleColor}
                            transition="color 0.2s">
                            Abonnement illimité
                        </Text>
                    </HStack>
                    <Text className="btn-text" fontSize={["sm", "lg"]} fontWeight="bold"
                        color={selectedSubscription === "unlimited" ? unlimitedActiveTextColor : textColor}
                        transition="color 0.2s">180€ HT / an</Text>
                    <HStack mt={2}><G1Icon.Check /><Text className="btn-text" fontSize={["xs", "sm"]} color={selectedSubscription === "unlimited" ? unlimitedActiveTextColor : textColor} transition="color 0.2s">Pour 1 utilisateur</Text></HStack>
                    <HStack><G1Icon.Check /><Text className="btn-text" fontSize={["xs", "sm"]} color={selectedSubscription === "unlimited" ? unlimitedActiveTextColor : textColor} transition="color 0.2s">Nombre de signatures illimitées</Text></HStack>
                    <HStack><G1Icon.Check /><Text className="btn-text" fontSize={["xs", "sm"]} color={selectedSubscription === "unlimited" ? unlimitedActiveTextColor : textColor} transition="color 0.2s">Inclus 1 mois gratuit</Text></HStack>
                </Box>
            </HStack>

            {$ok(selectedSubscription) && (
                <HStack alignSelf="flex-start">
                    <Text whiteSpace="nowrap" flexShrink={0}>Code partenaire :</Text>
                    <G1Input
                        component="connection.form"
                        value={partnerCode}
                        textTransform="uppercase"
                        onChange={(e) => onPartnerCodeChange(e.target.value)}
                    />
                </HStack>
            )}

            {$ok(selectedSubscription) && (
                <HStack spacing={4} alignSelf="flex-start">
                    <Text>Etes-vous le seul utilisateur du parapheur ?</Text>
                    <Button
                        size="sm" borderWidth="2px" borderRadius="lg"
                        borderColor={isAloneUser === true ? limitedBorderColor : "gray.200"}
                        bg={isAloneUser === true ? limitedBgColor : "transparent"}
                        color={isAloneUser === true ? limitedActiveTextColor : textColor}
                        _hover={{ borderColor: limitedBorderColor, bg: limitedBgColor, color: limitedActiveTextColor }}
                        transition="all 0.2s"
                        onClick={() => onAloneUserChange(true)}
                    >Oui</Button>
                    <Button
                        size="sm" borderWidth="2px" borderRadius="lg"
                        borderColor={isAloneUser === false ? limitedBorderColor : "gray.200"}
                        bg={isAloneUser === false ? limitedBgColor : "transparent"}
                        color={isAloneUser === false ? limitedActiveTextColor : textColor}
                        _hover={{ borderColor: limitedBorderColor, bg: limitedBgColor, color: limitedActiveTextColor }}
                        transition="all 0.2s"
                        onClick={() => onAloneUserChange(false)}
                    >Non</Button>
                </HStack>
            )}

            {$ok(selectedSubscription) && isAloneUser === false && (
                <HStack align="stretch" alignSelf="flex-start" alignItems="center" mt={2}>
                    <Text>Nombre de collaborateurs supplémentaires :</Text>
                    <NumberInput value={nbCollaborators} min={0} step={1} onChange={onCollaboratorsChange} size="md">
                        <NumberInputField
                            minW="70px"
                            width={`${Math.max(70, nbCollaborators.toString().length * 16 + 40)}px`}
                            textAlign="center"
                            fontWeight="semibold"
                            fontSize="md"
                            borderRadius="lg"
                            borderWidth="2px"
                            bg={$g1Color(`form.field.bg`)}
                            color={$g1Color(`form.field.write`)}
                            borderColor={$g1Color(`form.field.border`)}
                            _hover={{ borderColor: $g1Color(`form.field.hover.border`) }}
                            _focus={{ borderColor: $g1Color(`form.field.select.border`), boxShadow: "none" }}
                        />
                        <NumberInputStepper borderWidth="0" flexDirection="column" width="28px">
                            <NumberIncrementStepper border="none" borderRadius="md" mx="1" my="0.5" bg="blue.400" color="white" fontSize="xs" fontWeight="bold" _hover={{ bg: "blue.500", transform: "scale(1.1)" }} _active={{ bg: "blue.600", transform: "scale(0.95)" }} transition="all 0.15s">＋</NumberIncrementStepper>
                            <NumberDecrementStepper border="none" borderRadius="md" mx="1" my="0.5" bg="gray.200" color="gray.600" fontSize="xs" fontWeight="bold" _hover={{ bg: "gray.300", transform: "scale(1.1)" }} _active={{ bg: "gray.400", transform: "scale(0.95)" }} transition="all 0.15s">－</NumberDecrementStepper>
                        </NumberInputStepper>
                    </NumberInput>
                    <Text>* 60€ HT</Text>
                </HStack>
            )}
        </VStack>
    );
};